import { create } from 'zustand'

import { API_PATCH_UpdateUser, type UserT as AuthUserT } from '@/apis/auth.api'
import { API_UPDATE_UserNpsFirstAnswer, API_UPDATE_UserRenewalRewardClaim } from '@/apis/users.api'
import { setGa4UserId } from '@/utils/analytics'

import { registerStoreReset } from './reset.store'

export type UserT = AuthUserT

type T = {
  init: () => Promise<void>

  user: UserT | null
  setUser: (user: UserT | null) => void

  isNoBookCredits: boolean
  setIsNoBookCredits: (isAccFrozen: boolean) => void
  markNpsAfterFirstAnswerAsSeen: () => Promise<void>
  claimRenewalReward: () => Promise<void>
}

export const useStoreUser = create<T>((set, get): T => {
  return {
    init: async () => {
      const user = await API_PATCH_UpdateUser({ lastVisit: new Date().toISOString() })
      set({ user })
      if (user) {
        if (user.externalInfo?.code) setGa4UserId(user.externalInfo.code)
        const { detectLang, applyLang, normalizeLang } = await import('@/utils/i18n.util')
        const locale = normalizeLang(user.locale || detectLang())
        await applyLang(locale)
        if (user.locale !== locale) {
          // Persisting the corrected locale is a write-back, not something the first render
          // depends on — awaiting it added a second blocking PATCH for every non-English user.
          void API_PATCH_UpdateUser({ locale })
            .then((updatedUser) => {
              if (updatedUser) set({ user: updatedUser })
            })
            .catch((e) => console.error('Error persisting user locale:', e))
        }
      }
    },

    user: null,
    setUser: (user) => {
      set({ user })
    },

    isNoBookCredits: false,
    setIsNoBookCredits: (isNoBookCredits) => {
      set({ isNoBookCredits })
    },

    markNpsAfterFirstAnswerAsSeen: async () => {
      const currentUser = get().user
      if (!currentUser || currentUser.hasSeenNpsAfterFirstAnswer) return

      set({ user: { ...currentUser, hasSeenNpsAfterFirstAnswer: true } })

      try {
        const user = await API_UPDATE_UserNpsFirstAnswer(true)
        set({ user })
      } catch (err) {
        const latestUser = get().user
        if (latestUser) {
          set({ user: { ...latestUser, hasSeenNpsAfterFirstAnswer: false } })
        }
        throw err
      }
    },

    claimRenewalReward: async () => {
      const currentUser = get().user
      if (!currentUser?.showRenewalRewardModal) return

      set({ user: { ...currentUser, showRenewalRewardModal: false } })
      const user = await API_UPDATE_UserRenewalRewardClaim()
      set({ user })
    },
  }
})

registerStoreReset(useStoreUser)
